import React, { memo, useState, useCallback } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Label } from '@/components/ui/label';
import { FileText, Copy, Download, Trash2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import ReactMarkdown from 'react-markdown';

interface OutputNodeData {
  label: string;
  output: string;
}

interface OutputNodeProps extends NodeProps {
  data: OutputNodeData;
}

export const OutputNode = memo(({ data, selected, id }: OutputNodeProps) => {
  const [showToolbar, setShowToolbar] = useState(false);
  const { getNodes, setNodes, getEdges, setEdges } = useReactFlow();
  
  const handleCopy = () => {
    if (data.output) {
      navigator.clipboard.writeText(data.output);
      toast({
        title: 'Copied to clipboard',
        description: 'Output has been copied to your clipboard',
      });
    }
  };

  const handleDuplicate = useCallback(() => {
    const nodes = getNodes();
    const currentNode = nodes.find(node => node.id === id);
    if (currentNode) {
      const newNode = {
        ...currentNode,
        id: `${currentNode.type}_${Date.now()}`,
        position: {
          x: currentNode.position.x + 50,
          y: currentNode.position.y + 50,
        },
      };
      setNodes([...nodes, newNode]);
      // toast({
      //   title: "Node duplicated",
      //   description: "The node has been duplicated successfully",
      // });
    }
  }, [id, getNodes, setNodes]);


  const handleDelete = useCallback(() => {
    const nodes = getNodes();
    const edges = getEdges();
    
    const updatedNodes = nodes.filter(node => node.id !== id);
    const updatedEdges = edges.filter(edge => edge.source !== id && edge.target !== id);
    
    setNodes(updatedNodes);
    setEdges(updatedEdges);
    
    // toast({
    //   title: "Node deleted",
    //   description: "The node and its connections have been removed",
    // });
  }, [id, getNodes, getEdges, setNodes, setEdges]);

  const hasOutput = data.output && data.output.trim().length > 0;

  return (
    <div 
      className="relative group"
      onMouseEnter={() => setShowToolbar(true)}
      onMouseLeave={() => setShowToolbar(false)}
    >
      {/* Gumloop-style Toolbar */}
      {(showToolbar || selected) && (
        <div className="absolute -top-10 left-1/2 -translate-x-1/2 z-10">
          <div className="flex items-center gap-1 bg-background/95 backdrop-blur-sm border rounded-lg shadow-lg px-2 py-1">
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 hover:bg-muted"
              onClick={handleDuplicate}
            >
              <Copy className="w-3 h-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 hover:bg-destructive hover:text-destructive-foreground"
              onClick={handleDelete}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
      )}

      <Card 
        className={`min-w-[320px] backdrop-blur-md bg-background/75 border-red-500/30 transition-all duration-200 ${
          selected 
            ? 'ring-2 ring-red-500/50 shadow-lg shadow-red-500/20' 
            : 'shadow-md hover:shadow-lg'
        }`}
      >
        {/* Header */}
        <div className="p-4 pb-2">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
              <FileText className="w-4 h-4 text-red-400" />
            </div>
            <div className="flex-1">
              <div className="font-medium text-sm text-foreground">
                {data.label || 'Output'}
              </div>
              <div className="text-xs text-muted-foreground">
                Workflow result
              </div>
            </div>
            {hasOutput && (
              <div className="flex gap-1">
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={handleCopy}
                  className="h-7 w-7 p-0 hover:bg-red-500/10"
                >
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="px-4 pb-4">
          <div className="space-y-2">
            <Label className="text-xs font-medium">
              Result
            </Label>
            <ScrollArea className="h-[140px] w-full rounded-md border border-border/50 bg-background/30">
              <div className="p-3">
                {hasOutput ? (
                  <div className="text-sm prose prose-sm dark:prose-invert max-w-none">
                    <ReactMarkdown
                      components={{
                        p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                        code: ({ children }) => (
                          <code className="bg-muted px-1 py-0.5 rounded text-xs">{children}</code>
                        ),
                        pre: ({ children }) => (
                          <pre className="bg-muted p-2 rounded text-xs overflow-x-auto">{children}</pre>
                        ),
                      }}
                    >
                      {data.output}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground italic flex items-center justify-center h-full">
                    <div className="text-center">
                      <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
                      <div>No output yet...</div>
                      <div className="text-xs mt-1">Run the workflow to see results</div>
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </div>
        
        {/* Input Handle - Positioned on left edge */}
        <Handle
          type="target"
          position={Position.Left}
          id="input"
          className="custom-handle"
          style={{
            backgroundColor: '#ef4444',
            boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)',
          }}
        />
      </Card>
    </div>
  );
});

OutputNode.displayName = 'OutputNode';
